import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  alpha,
  useTheme
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import {
  useConnectCustomImapSmtpAccountMutation,
  useTestImapMutation,
  useTestSmtpMutation
} from 'src/services/account-service'
import { useSelector } from 'react-redux'

// Utility to safely get nested object properties
const get = (obj, path) => path.split('.').reduce((o, key) => o?.[key], obj)
const smtpAllowedPorts = [25, 465, 587, 2525, 8025]
const imapAllowedPorts = [143, 993]
const validHostnameRegex = /^(?!-)(?=.*[A-Za-z])[A-Za-z0-9-]{1,63}(?<!-)(\.[A-Za-z0-9-]{1,63})*\.[A-Za-z]{2,}$/
const validIpv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/

// Validation schema
const validationSchema = Yup.object({
  name: Yup.object({
    first: Yup.string().required('First Name is required'),
    last: Yup.string().required('Last Name is required')
  }),
  username: Yup.string()
    .email('Must be a valid email')
    .required('Email is required')
    .test('no-plus', 'Please add a valid email address', (value) => {
      return !value || !value.includes('+')
    }),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters long')
    .max(128, 'Password must be no more than 128 characters long')
    .required('Password is required')
    .test(
      'no-whitespace',
      'Password must not contain any spaces',
      val => val && !/\s/.test(val)
    ),
  imapHost: Yup.string()
    .required('IMAP Host is required')
    .test(
      'no-leading-trailing-spaces',
      'IMAP Host must not have leading or trailing spaces',
      val => val && val === val.trim()
    )
    .test(
      'valid-host-or-ip',
      'IMAP Host must be a valid hostname or IPv4 address',
      val => val && (validHostnameRegex.test(val) || validIpv4Regex.test(val))
    ),

  smtpHost: Yup.string()
    .required('IMAP Host is required')
    .test(
      'no-leading-trailing-spaces',
      'IMAP Host must not have leading or trailing spaces',
      val => val && val === val.trim()
    )
    .test(
      'valid-host-or-ip',
      'IMAP Host must be a valid hostname or IPv4 address',
      val => val && (validHostnameRegex.test(val) || validIpv4Regex.test(val))
    ),

  smtpPort: Yup.number()
    .required('SMTP Port is required')
    .typeError('SMTP Port must be a number')
    .oneOf(smtpAllowedPorts, `SMTP Port must be one of: ${smtpAllowedPorts.join(', ')}`),

  imapPort: Yup.number()
    .required('IMAP Port is required')
    .typeError('IMAP Port must be a number')
    .oneOf(imapAllowedPorts, `IMAP Port must be one of: ${imapAllowedPorts.join(', ')}`)
})

// Field configuration
const fields = [
  { name: 'name.first', label: 'First Name', placeholder: 'First name' },
  { name: 'name.last', label: 'Last Name', placeholder: 'Last name' },
  { name: 'username', label: 'Custom SMTP Email', placeholder: 'no@replaymail.com', type: 'email' },
  { name: 'password', label: 'Password', placeholder: '6sf7shs8fsg6wh89j' },
  { name: 'imapHost', label: 'IMAP Host', placeholder: 'imap.host.com' },
  { name: 'imapPort', label: 'Imap Port', placeholder: '993' },
  { name: 'smtpHost', label: 'SMTP Host', placeholder: 'smtp.host.com' },
  { name: 'smtpPort', label: 'Smtp Port', placeholder: '587' }
]

const ConnectWithPrivateDomain = ({ onCancel }) => {
  const theme = useTheme()
  const [testImap] = useTestImapMutation()
  const [testSmtp] = useTestSmtpMutation()
  const [connectCustomImapSmtpAccount] = useConnectCustomImapSmtpAccountMutation()
  const workspace = useSelector((state) => state.workspace)
  const [workspaceId, setWorkspaceId] = useState()

  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setWorkspaceId(workspace?._id)
  }, [workspace])

  const handleSubmit = async (values) => {
    try {
      setLoading(true)
      const imapData = {
        username: values.username,
        password: values.password,
        host: values.imapHost,
        port: values.imapPort
      }
      const imapResult = await testImap(imapData).unwrap()

      if (imapResult) {
        toast.success(`IMAP ${imapResult.message}`)

        const smtpData = {
          username: values.username,
          password: values.password,
          host: values.smtpHost,
          port: values.smtpPort
        }

        const smtpResult = await testSmtp(smtpData).unwrap()

        if (smtpResult) {
          toast.success(`SMTP ${smtpResult.message}`)

          const data = {
            name: values.name,
            email: values.username,
            imap: imapData,
            smtp: smtpData,
            workspaceId,
            isWhitelabelCustomSMTPMail: true
          }

          const { message } = await connectCustomImapSmtpAccount({
            data,
            reconnect: false,
            iframe: false
          }).unwrap()

          toast.success(message)
          formik.resetForm()
        }
      }
    } catch (err) {
      toast.error(`${err?.status || 'Error'}: ${err.data.error.message || 'Falied to update smtp email !'}`)
    } finally {
      setLoading(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      name: {
        first: '',
        last: ''
      },
      username: '',
      password: '',
      imapHost: '',
      smtpHost: '',
      imapPort: '',
      smtpPort: ''
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmit
  })

  return (
    <>
      <Grid container spacing={2}>
        {fields.map(({ name, label, placeholder, type = 'text' }) => (
          <Grid key={name} item xs={12} sm={6}>
            <Typography variant='subtitle2' sx={{ mb: 1, color: theme.palette.primary.main }}>
              {label}
            </Typography>
            <TextField
              name={name}
              placeholder={placeholder}
              type={type}
              variant='outlined'
              size='small'
              fullWidth
              value={get(formik.values, name) || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(get(formik.touched, name) && get(formik.errors, name))}
              helperText={get(formik.touched, name) && get(formik.errors, name)}
            />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, mb: 2 }}>
        <Button
          variant='contained'
          size='small'
          onClick={formik.handleSubmit}
          disabled={loading}
          sx={{
            bgcolor: theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.9)
            }
          }}
          className='text-end'
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>

        <Button
          variant='contained'
          size='small'
          onClick={onCancel}
          sx={{
            bgcolor: theme.palette.error.main,
            margin: '5px',
            '&:hover': {
              bgcolor: alpha(theme.palette.error.light, 0.9)
            }
          }}
          className='text-end'
        >
          Cancel
        </Button>
      </Box>
    </>
  )
}

export default ConnectWithPrivateDomain
